import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, ChevronRight, Sparkles, Zap,
  Timer, Receipt,
} from "lucide-react";
import { getSupplierBySlug, SUPPLIER_LANDINGS } from "@/data/supplierLandings";
import { getPostBySlug } from "@/data/blogPosts";

const DASABO_LOGO = "/logo.png";
const SITE_URL = "https://autofattura.dasabo.com";

function useSupplierJsonLd(entry) {
  React.useEffect(() => {
    if (!entry) return undefined;
    const ids = [];

    // WebPage + Product (SaaS solution for this supplier)
    const page = document.createElement("script");
    page.type = "application/ld+json";
    page.id = "jsonld-supplier-page";
    page.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `Autofattura per ${entry.supplier.brand}`,
      description: entry.seoDescription,
      url: `${SITE_URL}/autofattura/${entry.slug}`,
      inLanguage: "it",
      isPartOf: { "@type": "WebSite", name: "Autofattura by Dasabo", url: SITE_URL },
      about: {
        "@type": "Organization",
        name: entry.supplier.name,
        vatID: entry.supplier.vatId,
        address: entry.supplier.address,
      },
    });
    document.head.appendChild(page);
    ids.push(page.id);

    // Breadcrumb
    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = "jsonld-supplier-breadcrumb";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: `Autofattura ${entry.supplier.brand}`, item: `${SITE_URL}/autofattura/${entry.slug}` },
      ],
    });
    document.head.appendChild(bc);
    ids.push(bc.id);

    // FAQPage
    if (entry.faq?.length) {
      const faq = document.createElement("script");
      faq.type = "application/ld+json";
      faq.id = "jsonld-supplier-faq";
      faq.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: entry.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(faq);
      ids.push(faq.id);
    }

    return () => ids.forEach((id) => document.getElementById(id)?.remove());
  }, [entry]);
}

export default function SupplierLanding() {
  const { slug } = useParams();
  const entry = getSupplierBySlug(slug);

  useSeo({
    title: entry ? `Autofattura ${entry.supplier.brand} · TD17 automatica` : "Fornitore non trovato",
    description: entry?.seoDescription || "",
    path: `/autofattura/${slug}`,
    noindex: !entry,
  });
  useSupplierJsonLd(entry);

  if (!entry) return <Navigate to="/" replace />;

  const relatedPost = entry.relatedPostSlug ? getPostBySlug(entry.relatedPostSlug) : null;
  const otherSuppliers = SUPPLIER_LANDINGS.filter((s) => s.slug !== entry.slug).slice(0, 4);
  const logoUrl = `https://cdn.simpleicons.org/${entry.supplier.logoSlug}`;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="supplier-landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E8E4D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] transition-colors" data-testid="supplier-back-link">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium text-[#4A4A4A]">
            <Link to="/blog" className="hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/docs" className="hover:text-[#0A0A0A]">Docs</Link>
            <Link to="/login" className="hover:text-[#0A0A0A]">Accedi</Link>
            <Link to="/register" className="px-4 py-1.5 rounded-full bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors">Prova gratis</Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-xs text-[#737373]">
        <ol className="flex items-center gap-1.5">
          <li><Link to="/" className="hover:text-[#0A0A0A]">Home</Link></li>
          <li><ChevronRight className="w-3 h-3 inline" /></li>
          <li className="text-[#0A0A0A] font-medium">Autofattura {entry.supplier.brand}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold mb-4" style={{ color: entry.supplier.brandColor }}>
            <span className="w-4 h-px" style={{ backgroundColor: entry.supplier.brandColor }} /> {entry.hero.kicker}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#0A0A0A] leading-[1.03]">
            {entry.hero.h1}
            <br />
            <span className="text-[#1D4ED8]">{entry.hero.h1Suffix}</span>
          </h1>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed max-w-xl">{entry.hero.subtitle}</p>

          <ul className="mt-8 space-y-2.5">
            {entry.hero.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[15px] text-[#0A0A0A]">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link
              to="/register"
              data-testid="supplier-hero-cta-primary"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A0A0A] text-white font-semibold text-sm hover:bg-[#1D4ED8] transition-colors"
            >
              Prova gratis con 5 crediti <ArrowRight className="w-4 h-4" />
            </Link>
            {relatedPost && (
              <Link
                to={`/blog/${relatedPost.slug}`}
                data-testid="supplier-hero-cta-guide"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#0A0A0A] text-[#0A0A0A] font-semibold text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                Leggi la guida completa <ArrowUpRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>

        {/* Preview card */}
        <SupplierPreview entry={entry} logoUrl={logoUrl} />
      </section>

      {/* Facts strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="rounded-3xl bg-white border border-[#E8E4D9] p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {entry.facts.map((f, i) => (
            <div key={i} className="text-center md:text-left" data-testid={`supplier-fact-${i}`}>
              <div className="text-xs uppercase tracking-wider text-[#737373] font-semibold mb-1.5">{f.label}</div>
              <div className="font-display font-black text-2xl tracking-tighter text-[#0A0A0A]">{f.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-orange-600 font-bold mb-4">
          <span className="w-4 h-px bg-orange-600" /> Il problema
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] mb-4">{entry.problem.title}</h2>
        <p className="text-lg text-[#4A4A4A] leading-relaxed">{entry.problem.body}</p>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
          <span className="w-4 h-px bg-[#1D4ED8]" /> La soluzione in 3 step
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] mb-10">
          {entry.supplier.brand} → autofattura in 30 secondi
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {entry.steps.map((s, i) => (
            <div key={i} className="bg-white border border-[#E8E4D9] rounded-2xl p-6" data-testid={`supplier-step-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] text-white grid place-items-center font-display font-black mb-4">{i + 1}</div>
              <h3 className="font-display font-bold text-xl tracking-tight text-[#0A0A0A] mb-2">{s.title}</h3>
              <p className="text-[#4A4A4A] leading-relaxed text-sm">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {entry.faq?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
            <span className="w-4 h-px bg-[#1D4ED8]" /> Domande su {entry.supplier.brand}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] mb-8">
            Le domande più frequenti
          </h2>
          <div className="space-y-3">
            {entry.faq.map((f, i) => (
              <details key={i} data-testid={`supplier-faq-${i}`} className="group bg-white border border-[#E8E4D9] rounded-2xl p-5">
                <summary className="cursor-pointer font-semibold text-[#0A0A0A] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-[#4A4A4A] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Other suppliers cross-link */}
      {otherSuppliers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
            <span className="w-4 h-px bg-[#1D4ED8]" /> Altri fornitori supportati
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {otherSuppliers.map((s) => (
              <Link
                key={s.slug}
                to={`/autofattura/${s.slug}`}
                data-testid={`supplier-cross-${s.slug}`}
                className="group bg-white border border-[#E8E4D9] rounded-2xl p-5 hover:border-[#0A0A0A]/40 hover:shadow-lg transition-all text-center"
              >
                <img
                  src={`https://cdn.simpleicons.org/${s.supplier.logoSlug}`}
                  alt={s.supplier.brand}
                  className="w-8 h-8 mx-auto mb-3 opacity-60 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
                <div className="font-display font-bold text-sm text-[#0A0A0A]">{s.supplier.brand}</div>
                <div className="text-[10px] text-[#737373] font-mono mt-1">{s.supplier.vatId}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-[#0A0A0A] text-white p-8 sm:p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">
              La tua prossima autofattura {entry.supplier.brand}: 30 secondi.
            </h3>
            <p className="text-white/70 max-w-xl leading-relaxed">
              5 crediti gratuiti al signup. Carta di credito non richiesta. Un solo click per trasmettere allo SDI.
            </p>
          </div>
          <Link
            to="/register"
            data-testid="supplier-final-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0A0A0A] font-semibold text-sm hover:bg-[#F97316] hover:text-white transition-colors whitespace-nowrap"
          >
            Prova gratis <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-[#737373] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2 text-white font-display font-extrabold text-base">
            Autofattura by
            <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto brightness-0 invert" />
          </div>
          <div>© 2026 DASABO SRL · P.IVA IT07407380828 · Palermo</div>
        </div>
      </footer>
    </div>
  );
}

// ---- Preview card component --------------------------------------------

function SupplierPreview({ entry, logoUrl }) {
  const ex = entry.example;
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br rounded-[3rem] blur-2xl" style={{ backgroundImage: `linear-gradient(135deg, ${entry.supplier.brandColor}30, transparent 60%, #1D4ED820)` }} />
      <div className="relative rounded-3xl bg-white border border-[#E8E4D9] shadow-2xl shadow-[#0A0A0A]/10 p-6 md:p-7 z-10">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E8E4D9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black" style={{ backgroundColor: entry.supplier.brandColor }}>
              {ex.logo}
            </div>
            <div>
              <div className="font-semibold text-[#0A0A0A] leading-tight">{ex.supplier}</div>
              <div className="text-xs text-[#737373] font-mono">{ex.vatId}</div>
            </div>
          </div>
          <span className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">
            Estratto in 12s
          </span>
        </div>
        <div className="space-y-2.5 text-sm">
          <RowKV k="Nr. fattura" v={ex.invoiceNr} mono />
          <RowKV k="Data" v={ex.date} mono />
          <RowKV k="Imponibile" v={ex.imponibile} mono bold />
          <RowKV k="IVA (Rev.Charge)" v={ex.iva} mono />
          <div className="flex justify-between py-2 border-t border-[#E8E4D9] mt-1">
            <span className="text-[#737373]">Tipo doc.</span>
            <span className="font-mono font-bold text-[#1D4ED8]">{ex.td}</span>
          </div>
        </div>
        <button
          type="button"
          disabled
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold cursor-default"
        >
          <Zap className="w-4 h-4" /> Invia allo SDI
        </button>
      </div>

      {/* Floating logo real */}
      <div className="absolute -top-6 -right-4 md:-right-8 rounded-2xl bg-white border border-[#E8E4D9] shadow-xl p-4 z-20 max-w-[200px] rotate-3" style={{ transform: "rotate(3deg)" }}>
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles className="w-4 h-4" style={{ color: entry.supplier.brandColor }} />
          <span className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">Fornitore</span>
        </div>
        <img src={logoUrl} alt={entry.supplier.brand} className="h-6 w-auto opacity-80" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <div className="font-display font-extrabold text-lg tracking-tighter text-[#0A0A0A] mt-1">{entry.supplier.brand}</div>
      </div>

      {/* Floating stat */}
      <div className="absolute -bottom-6 -left-4 md:-left-6 rounded-2xl bg-[#0A0A0A] text-white border border-[#0A0A0A] shadow-xl p-4 z-20 max-w-[220px]" style={{ transform: "rotate(-2deg)" }}>
        <div className="flex items-center gap-2 mb-1.5">
          <Timer className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Tempo salvato</span>
        </div>
        <div className="font-display font-extrabold text-2xl tracking-tighter">15 min → 30 s</div>
        <div className="flex items-center gap-1 text-xs text-white/60 mt-1"><Receipt className="w-3 h-3" /> per ogni fattura</div>
      </div>
    </div>
  );
}

function RowKV({ k, v, mono, bold }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-[#737373] text-sm">{k}</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-[#0A0A0A]" : "text-[#0A0A0A]"} text-sm`}>{v}</span>
    </div>
  );
}
